import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import paymentsService from '../services/paymentsService';
import styles from '../assets/paymentCreate.module.css';

const PaymentCreate = () => {
  const { rentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  // Refs for date picker inputs
  const dueDateInputRef = useRef(null);
  const paymentDateInputRef = useRef(null);

  const [formData, setFormData] = useState({
    rent_id: rentId,
    amount: '',
    amount_paid: '',
    due_date: '',
    payment_date: '',
    bank_account_id: '',
    status: '0', // Default to "Not paid"
    notes: '',
    redirectUrl: ''
  });

  // Date formatting functions matching Laravel dd/mm/yyyy format
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatDateForBackend = (dateString) => {
    if (!dateString) return null;
    // If already in yyyy-mm-dd format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
    // Convert from dd/mm/yyyy to yyyy-mm-dd
    const parts = dateString.split('/');
    if (parts.length !== 3) return null;
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  };

  // Convert DD/MM/YYYY to YYYY-MM-DD for native date input
  const convertToDateInputFormat = (dateString) => {
    if (!dateString) return '';
    // If already in YYYY-MM-DD format, return as is
    if (dateString.includes('-') && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    // Handle DD/MM/YYYY format (Laravel format)
    if (dateString.includes('/')) {
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const [day, month, year] = parts;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    }
    return '';
  };

  // Convert YYYY-MM-DD to DD/MM/YYYY (Laravel format)
  const convertFromDateInputFormat = (dateString) => {
    if (!dateString) return '';
    if (dateString.includes('-')) {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    }
    return dateString;
  };

  // Handle date change from native date picker
  const handleDateChange = (fieldName, e) => {
    const dateValue = e.target.value;
    if (dateValue) {
      const formattedDate = convertFromDateInputFormat(dateValue);
      setFormData(prev => ({
        ...prev,
        [fieldName]: formattedDate
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }
  };

  // Open date picker
  const openDatePicker = (fieldName, e) => {
    e?.preventDefault();
    let dateInputRef;
    if (fieldName === 'due_date') {
      dateInputRef = dueDateInputRef;
    } else if (fieldName === 'payment_date') {
      dateInputRef = paymentDateInputRef;
    }
    
    if (dateInputRef?.current) {
      setTimeout(() => {
        try {
          if (dateInputRef.current.showPicker) {
            dateInputRef.current.showPicker();
          } else {
            dateInputRef.current.click();
          }
        } catch (error) {
          dateInputRef.current.click();
        }
      }, 0);
    }
  };

  const getTodayFormatted = () => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    return `${day}/${month}/${year}`;
  };
  const [errors, setErrors] = useState({});

  // Helper function to safely render object properties as strings
  const safeRender = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'object') {
      // If it's an object with a name property, use that
      if (value.name) return value.name;
      // If it's an object with an id property, use that
      if (value.id) return value.id.toString();
      // If it's an object with a fullName property, use that
      if (value.fullName) return value.fullName;
      // If it's an object with a property_id property, use that
      if (value.property_id) return value.property_id;
      // Otherwise, return a dash to avoid rendering objects
      return '-';
    }
    return value.toString();
  };

  useEffect(() => {
    loadCreateData();
  }, [rentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCreateData = async () => {
    try {
      setLoading(true);
      const response = await paymentsService.getPaymentCreateForm(rentId);
      
      if (response.success) {
        console.log('🔍 [DEBUG] PaymentCreate API response:', response.data);
        setData(response.data);
        
        // Populate form with actual data from API response (using dd/mm/yyyy format)
        setFormData(prev => ({
          ...prev,
          rent_id: rentId,
          amount: response.data.rent?.monthly_payment?.toString() || '500.00',
          amount_paid: response.data.rent?.monthly_payment?.toString() || '500.00',
          due_date: formatDateForDisplay(response.data.rent?.date_start) || getTodayFormatted(),
          payment_date: getTodayFormatted(), // Default to today in dd/mm/yyyy
          bank_account_id: response.data.bankAccounts ? Object.keys(response.data.bankAccounts)[0] || '' : '',
          status: '0', // Default to "Not paid"
          notes: '',
          redirectUrl: (() => {
            const defaultUrl = `/rents/${rentId}/payments`;
            const url = response.data.redirectUrl || defaultUrl;
            console.log('🔍 [DEBUG] Backend redirectUrl:', url);

            // If URL is empty, /, or just whitespace, use default
            if (!url || url === '/' || url.trim() === '') {
              console.log('🔍 [DEBUG] Using default URL (empty/root detected)');
              return defaultUrl;
            }

            // Extract pathname if full URL
            if (url.startsWith('http://') || url.startsWith('https://')) {
              try {
                let pathname = new URL(url).pathname;
                // Remove /api/ prefix if present (backend might return API URL)
                if (pathname.startsWith('/api/')) {
                  pathname = pathname.replace('/api/', '/');
                }
                console.log('🔍 [DEBUG] Extracted pathname:', pathname);
                // If result is just /, use default
                if (pathname === '/' || pathname.trim() === '') {
                  return defaultUrl;
                }
                return pathname;
              } catch {
                return defaultUrl;
              }
            }

            // If it's already a path, remove /api/ prefix if present
            let finalUrl = url.startsWith('/api/') ? url.replace('/api/', '/') : url;

            // If result is just /, use default
            if (finalUrl === '/' || finalUrl.trim() === '') {
              console.log('🔍 [DEBUG] Using default URL (root detected after processing)');
              return defaultUrl;
            }

            console.log('🔍 [DEBUG] Final redirectUrl:', finalUrl);
            return finalUrl;
          })()
        }));
      } else {
        throw new Error(response.message || "Failed to load payment create form data");
      }
    } catch (err) {
      console.error("❌ [FRONTEND ERROR] Error loading payment create data:", err);
      setError(err.response?.data?.message || err.message || "Failed to load payment create form data");
      setData(null);
    } finally {
      setLoading(false);
    }
  };


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required field validation (matching Laravel CreatePaymentRequest)
    if (!formData.amount) newErrors.amount = 'Amount is required';
    if (!formData.amount_paid) newErrors.amount_paid = 'Amount paid is required';
    if (!formData.bank_account_id) newErrors.bank_account_id = 'Bank account is required';
    if (formData.status === '') newErrors.status = 'Status is required';

    // Due date is required if rent has date_start (matching Laravel logic)
    if (data?.rent?.date_start && !formData.due_date) {
      newErrors.due_date = 'Due date is required';
    }

    // Numeric validation
    if (formData.amount && isNaN(parseFloat(formData.amount))) {
      newErrors.amount = 'Amount must be a number';
    }
    if (formData.amount_paid && isNaN(parseFloat(formData.amount_paid))) {
      newErrors.amount_paid = 'Amount paid must be a number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors below');
      return;
    }

    try {
      setSubmitting(true);

      // Backend validation expects dd/mm/yyyy format, so send as is (no conversion)
      // Backend controller will convert to yyyy-mm-dd for database storage
      const submitData = {
        ...formData,
        due_date: formData.due_date || null, // Keep dd/mm/yyyy format for validation
        payment_date: formData.payment_date || null // Keep dd/mm/yyyy format for validation
      };

      const response = await paymentsService.createPayment(rentId, submitData);
      
      if (response && response.success) {
        toast.success(response.message || "Payment was successfully added");
        // Simple redirect - no delays, no complex logic
        navigate(`/rents/${rentId}/payments`, { 
          replace: false 
        });
      } else {
        const errorMessage = response?.message || "Failed to create payment";
        toast.error(errorMessage);
      }
    } catch (error) {
      // This catch block should rarely be hit now since createPayment returns error object
      console.error('❌ [PAYMENT CREATE] Unexpected error:', error);
      toast.error(error.response?.data?.message || error.message || "Failed to create payment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    const redirectUrl = formData.redirectUrl || `/rents/${rentId}/payments`;
    console.log('🔍 [DEBUG] Cancel button clicked, navigating to:', redirectUrl);
    console.log('🔍 [DEBUG] rentId:', rentId);
    console.log('🔍 [DEBUG] formData.redirectUrl:', formData.redirectUrl);
    navigate(redirectUrl);
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading payment create form...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={() => loadCreateData()} className={styles.btnRetry}>
            Retry
          </button>
          <button onClick={() => navigate(`/rents/${rentId}/payments`)} className={styles.btnBack}>
            Back to Payments
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <h3>No Data Available</h3>
          <p>Unable to load payment create form data.</p>
          <button onClick={() => loadCreateData()} className={styles.btnRetry}>
            Retry
          </button>
          <button onClick={() => navigate(`/rents/${rentId}/payments`)} className={styles.btnBack}>
            Back to Payments
          </button>
        </div>
      </div>
    );
  }

  // Debug logging to see the actual data structure
  console.log('🔍 [DEBUG] PaymentCreate rendering with data:', {
    property: data?.rent?.room?.property,
    client: data?.rent?.room?.property?.client,
    room: data?.rent?.room,
    guardian: data?.rent?.guardian,
    status: data?.rent?.guardian?.status
  });

  return (
    <div className={styles.container}>
      {/* Breadcrumb Navigation */}
      <div className={styles.breadcrumbWrapper}>
        <ol className={styles.breadcrumb}>
          <li className={styles.breadcrumbItem}>
            <span onClick={() => navigate('/dashboard')} className={styles.breadcrumbLink} style={{ cursor: 'pointer', color: '#337ab7' }}>Dashboard</span>
          </li>
          <li className={styles.breadcrumbSeparator}>/</li>
          <li className={styles.breadcrumbItem}>
            <span onClick={() => navigate('/properties')} className={styles.breadcrumbLink} style={{ cursor: 'pointer', color: '#337ab7' }}>Properties</span>
          </li>
          <li className={styles.breadcrumbSeparator}>/</li>
          <li className={styles.breadcrumbItem}>
            <span onClick={() => navigate(`/properties/${data?.rent?.room?.property?.id || ''}`)} className={styles.breadcrumbLink} style={{ cursor: 'pointer', color: '#337ab7' }}>{safeRender(data?.rent?.room?.property?.name) || 'Property'}</span>
          </li>
          <li className={styles.breadcrumbSeparator}>/</li>
          <li className={styles.breadcrumbItem}>
            <span onClick={() => navigate(`/rents/${rentId}/payments`)} className={styles.breadcrumbLink} style={{ cursor: 'pointer', color: '#337ab7' }}>Payment List</span>
          </li>
          <li className={styles.breadcrumbSeparator}>/</li>
          <li className={`${styles.breadcrumbItem} ${styles.breadcrumbActive}`}>Payment</li>
        </ol>
      </div>

      {/* Guardian Overview Section */}
      <div className={styles.guardianOverviewSection}>
        <div className={styles.row}>
          <div className={styles.colMd12}>
            <div className={styles.panelTitle}>
              <h3 className={styles.panelTitleText}>Guardian overview</h3>
            </div>
            <div className={styles.panelGroup}>
              <div className={styles.row}>
                <div className={styles.colSm6}>
                  <div className={styles.guardianAvatarWrapper}>
                    {data?.rent?.guardian?.image ? (
                      <img className={styles.guardianAvatar} src={data.rent.guardian.image} alt="Guardian" />
                    ) : (
                      <div className={styles.guardianAvatarPlaceholder}></div>
                    )}
                  </div>

                  <dl className={styles.greenList}>
                    <div className={styles.listRow}>
                      <dt>Guardian ID:</dt>
                      <dd>{safeRender(data?.rent?.guardian?.unique_guardian_number) || '-'}</dd>
                    </div>
                    <div className={styles.listRow}>
                      <dt>Name:</dt>
                      <dd>{safeRender(data?.rent?.guardian?.fullName) || '-'}</dd>
                    </div>
                    <div className={styles.listRow}>
                      <dt>Phone number:</dt>
                      <dd>{safeRender(data?.rent?.guardian?.phone_number) || '-'}</dd>
                    </div>
                    <div className={styles.listRow}>
                      <dt>Mobile number:</dt>
                      <dd>{safeRender(data?.rent?.guardian?.mobile_number) || '-'}</dd>
                    </div>
                    <div className={styles.listRow}>
                      <dt>Email:</dt>
                      <dd>
                        <a href={`mailto:${data?.rent?.guardian?.email || ''}`} className={styles.mailtoLink}>
                          {safeRender(data?.rent?.guardian?.email) || '-'}
                        </a>
                      </dd>
                    </div>
                    {data?.rent?.guardian?.hear_about && data.rent.guardian.hear_about !== '-' && (
                      <div className={styles.listRow}>
                        <dt title="How did you hear about Global Guardians?">Hear about GG:</dt>
                        <dd>{data.rent.guardian.hear_about}</dd>
                      </div>
                    )}
                  </dl>
                </div>
                <div className={styles.colSm6}>
                  <dl className={styles.greenList}>
                    <div className={styles.listRow}>
                      <dt>Status:</dt>
                      <dd>
                        <span 
                          className={styles.statusLabel}
                          style={{ backgroundColor: data?.rent?.guardian?.status?.color || '#28a745' }}
                          title={safeRender(data?.rent?.guardian?.status?.name) || 'Active'}
                        >
                          {safeRender(data?.rent?.guardian?.status?.name) || 'Active'}
                        </span>
                      </dd>
                    </div>
                    <div className={styles.listRow}>
                      <dt>Gender:</dt>
                      <dd>{safeRender(data?.rent?.guardian?.gender) || '-'}</dd>
                    </div>
                    <div className={styles.listRow}>
                      <dt title="Date of Birth">DoB:</dt>
                      <dd>{safeRender(data?.rent?.guardian?.date_of_birth) || '-'}</dd>
                    </div>
                    <div className={styles.listRow}>
                      <dt>Nationality:</dt>
                      <dd>{safeRender(data?.rent?.guardian?.nationality) || '-'}</dd>
                    </div>
                    <div className={styles.listRow}>
                      <dt>Religion:</dt>
                      <dd>-</dd>
                    </div>
                    <div className={styles.listRow}>
                      <dt title="National Insurance Number">NI:</dt>
                      <dd>{safeRender(data?.rent?.guardian?.nhs_number) || '-'}</dd>
                    </div>
                    <div className={styles.listRow}>
                      <dt>Next of kin:</dt>
                      <dd>{safeRender(data?.rent?.guardian?.next_of_kin) || '-'}</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Details Section (Property and Room) */}
      <div className={styles.detailsSection}>
        <div className={styles.row}>
          <div className={styles.colXs12}>
            <div className={styles.panelTitle}>
              <h3 className={styles.panelTitleText}>Details</h3>
            </div>
            <div className={styles.panelGroup}>
              <div className={styles.row}>
                <div className={styles.colXs6}>
                  <dl className={styles.greenList}>
                    <div className={styles.listRow}>
                      <dt>Property ID:</dt>
                      <dd>{safeRender(data?.rent?.room?.property?.property_id) || '-'}</dd>
                    </div>
                    <div className={styles.listRow}>
                      <dt>Property Name:</dt>
                      <dd>{safeRender(data?.rent?.room?.property?.name) || '-'}</dd>
                    </div>
                    <div className={styles.listRow}>
                      <dt>Property Status:</dt>
                      <dd>{safeRender(data?.rent?.room?.property?.status_text) || '-'}</dd>
                    </div>
                    <div className={styles.listRow}>
                      <dt>Property address:</dt>
                      <dd>{safeRender(data?.rent?.room?.property?.address_1) || '-'}</dd>
                    </div>
                    <div className={styles.listRow}>
                      <dt>Postcode:</dt>
                      <dd>{safeRender(data?.rent?.room?.property?.postcode) || '-'}</dd>
                    </div>
                  </dl>
                </div>
                <div className={styles.colXs6}>
                  <dl className={styles.greenList}>
                    <div className={styles.listRow}>
                      <dt>Client:</dt>
                      <dd>{safeRender(data?.rent?.room?.property?.client?.name) || '-'}</dd>
                    </div>
                    <div className={styles.listRow}>
                      <dt>GG Bank Account:</dt>
                      <dd>{safeRender(data?.rent?.room?.property?.client_account) || '-'}</dd>
                    </div>
                    <div className={styles.listRow}>
                      <dt>Room:</dt>
                      <dd>{safeRender(data?.rent?.room?.name) || '-'}</dd>
                    </div>
                    <div className={styles.listRow}>
                      <dt>Rate:</dt>
                      <dd>£{data?.rent?.monthly_payment?.toFixed(2) || data?.rent?.room?.price?.toFixed(2) || '0.00'}</dd>
                    </div>
                    <div className={styles.listRow}>
                      <dt>Start:</dt>
                      <dd>{formatDateForDisplay(data?.rent?.date_start) || formatDateForDisplay(data?.rent?.room?.property?.date_taken_on) || '-'}</dd>
                    </div>
                    <div className={styles.listRow}>
                      <dt>End:</dt>
                      <dd>{formatDateForDisplay(data?.rent?.date_end) || formatDateForDisplay(data?.rent?.room?.property?.date_taken_back) || '-'}</dd>
                    </div>
                    <div className={styles.listRow}>
                      <dt>Status:</dt>
                      <dd>
                        {data?.rent?.status === 0 || data?.rent?.status === '0' ? 'In progress' :
                         data?.rent?.status === 1 || data?.rent?.status === '1' ? 'Active' :
                         data?.rent?.status === 2 || data?.rent?.status === '2' ? 'Failed' :
                         data?.rent?.status === 3 || data?.rent?.status === '3' ? 'Ended' :
                         safeRender(data?.rent?.status) || 'Active'}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Form Section */}
      <div className={styles.paymentFormSection}>
        <div className={styles.row}>
          <div className={styles.colMd12}>
            <div className={styles.panelTitle}>
              <h3 className={styles.paymentHeader}>Payment</h3>
            </div>

            <div className={styles.panelGroup}>
              <form onSubmit={handleSubmit} className={styles.paymentForm}>
                <input type="hidden" name="rent_id" value={formData.rent_id} />
                <input type="hidden" name="redirectUrl" value={formData.redirectUrl} />

                <fieldset className={styles.formHorizontal}>
                  {/* Amount */}
                  <div className={`${styles.formGroup} ${errors.amount ? styles.hasError : ''}`}>
                    <label className={`${styles.controlLabel} ${styles.colSm5} ${styles.labelRequired}`}>Amount</label>
                    <div className={styles.colSm2}>
                      <input
                        type="text"
                        name="amount"
                        value={formData.amount}
                        onChange={handleInputChange}
                        className={styles.formControl}
                        placeholder="0.00"
                      />
                      {errors.amount && <span className={styles.helpBlock}>{errors.amount}</span>}
                    </div>
                  </div>

                  {/* Amount Paid */}
                  <div className={`${styles.formGroup} ${errors.amount_paid ? styles.hasError : ''}`}>
                    <label className={`${styles.controlLabel} ${styles.colSm5} ${styles.labelRequired}`}>Amount Paid</label>
                    <div className={styles.colSm2}>
                      <input
                        type="text"
                        name="amount_paid"
                        value={formData.amount_paid}
                        onChange={handleInputChange}
                        className={styles.formControl}
                        placeholder="0.00"
                      />
                      {errors.amount_paid && <span className={styles.helpBlock}>{errors.amount_paid}</span>}
                    </div>
                  </div>

                  {/* Due Date */}
                  <div className={`${styles.formGroup} ${errors.due_date ? styles.hasError : ''}`}>
                    <label className={`${styles.controlLabel} ${styles.colSm5} ${styles.labelRequired}`}>Due Date</label>
                    <div className={styles.colSm2} style={{ position: 'relative' }}>
                      <input
                        type="text"
                        name="due_date"
                        value={formData.due_date}
                        readOnly
                        onClick={(e) => openDatePicker('due_date', e)}
                        className={styles.formControl}
                        placeholder="dd/mm/yyyy"
                        style={{ paddingRight: '35px', cursor: 'pointer' }}
                      />
                      {/* Hidden date input for native date picker */}
                      <input
                        ref={dueDateInputRef}
                        type="date"
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          width: '100%',
                          height: '100%',
                          opacity: 0,
                          cursor: 'pointer',
                          zIndex: 10,
                          fontSize: '16px',
                          padding: '6px 35px 6px 10px',
                          margin: 0,
                          border: 'none',
                          background: 'transparent',
                          boxSizing: 'border-box'
                        }}
                        value={convertToDateInputFormat(formData.due_date)}
                        onChange={(e) => handleDateChange('due_date', e)}
                      />
                      <span
                        className="glyphicon glyphicon-calendar"
                        onClick={(e) => openDatePicker('due_date', e)}
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#999',
                          cursor: 'pointer',
                          pointerEvents: 'auto',
                          zIndex: 11,
                          fontSize: '14px'
                        }}
                      />
                      {errors.due_date && <span className={styles.helpBlock}>{errors.due_date}</span>}
                    </div>
                  </div>

                  {/* Payment Date */}
                  <div className={styles.formGroup}>
                    <label className={`${styles.controlLabel} ${styles.colSm5}`}>Payment Date</label>
                    <div className={styles.colSm2} style={{ position: 'relative' }}>
                      <input
                        type="text"
                        name="payment_date"
                        value={formData.payment_date}
                        readOnly
                        onClick={(e) => openDatePicker('payment_date', e)}
                        className={styles.formControl}
                        placeholder="dd/mm/yyyy"
                        style={{ paddingRight: '35px', cursor: 'pointer' }}
                      />
                      {/* Hidden date input for native date picker */}
                      <input
                        ref={paymentDateInputRef}
                        type="date"
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          width: '100%',
                          height: '100%',
                          opacity: 0,
                          cursor: 'pointer',
                          zIndex: 10,
                          fontSize: '16px',
                          padding: '6px 35px 6px 10px',
                          margin: 0,
                          border: 'none',
                          background: 'transparent',
                          boxSizing: 'border-box'
                        }}
                        value={convertToDateInputFormat(formData.payment_date)}
                        onChange={(e) => handleDateChange('payment_date', e)}
                      />
                      <span
                        className="glyphicon glyphicon-calendar"
                        onClick={(e) => openDatePicker('payment_date', e)}
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#999',
                          cursor: 'pointer',
                          pointerEvents: 'auto',
                          zIndex: 11,
                          fontSize: '14px'
                        }}
                      />
                    </div>
                  </div>

                  {/* GG Bank Account */}
                  <div className={`${styles.formGroup} ${errors.bank_account_id ? styles.hasError : ''}`}>
                    <label className={`${styles.controlLabel} ${styles.colSm5} ${styles.labelRequired}`}>GG Bank Account</label>
                    <div className={styles.colSm4}>
                      <select
                        name="bank_account_id"
                        value={formData.bank_account_id}
                        onChange={handleInputChange}
                        className={styles.formControl}
                        required
                      >
                        <option value="">Select Bank Account</option>
                        {Object.entries(data?.bankAccounts || {}).map(([id, name]) => (
                          <option key={id} value={id}>{safeRender(name)}</option>
                        ))}
                      </select>
                      {errors.bank_account_id && <span className={styles.helpBlock}>{errors.bank_account_id}</span>}
                    </div>
                  </div>

                  {/* Status */}
                  <div className={`${styles.formGroup} ${errors.status ? styles.hasError : ''}`}>
                    <label className={`${styles.controlLabel} ${styles.colSm5} ${styles.labelRequired}`}>Status</label>
                    <div className={styles.colSm2}>
                      <select
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        className={styles.formControl}
                      >
                        <option value="0">Not paid</option>
                        <option value="1">Paid</option>
                        <option value="4">Plan</option>
                        <option value="5">Legal</option>
                        <option value="6">Notice to quit</option>
                      </select>
                      {errors.status && <span className={styles.helpBlock}>{errors.status}</span>}
                    </div>
                  </div>

                  {/* Notes */}
                  <div className={styles.formGroup}>
                    <label className={`${styles.controlLabel} ${styles.colSm5}`}>Notes</label>
                    <div className={styles.colSm7}>
                      <textarea
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                        className={styles.formControl}
                        rows="4"
                        placeholder="Add any additional notes about this payment..."
                      />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className={styles.formGroup}>
                    <div className={styles.colSm12}>
                      <div className={styles.buttonGroup}>
                        <button
                          type="button"
                          className={`${styles.btn} ${styles.btnDefault}`}
                          onClick={handleCancel}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className={`${styles.btn} ${styles.btnPrimary}`}
                          disabled={submitting}
                        >
                          <i className="glyphicon glyphicon-ok"></i> {submitting ? 'Adding...' : 'Add'}
                        </button>
                      </div>
                    </div>
                  </div>
                </fieldset>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentCreate;